import { useMemo } from 'react';
import { useAtomValue } from 'jotai';
import {
  allVoiTemplates as allVoiTemplatesState,
  unitSystem as unitSystemState,
  customBlocksEnabled as customBlocksEnabledState,
  customBlocksNames as customBlocksNamesState,
} from '@/components/Analysis/state';
import {
  BLISTER_TYPE,
  DECK_KEY,
  heightTypeMapping,
  NOT_APPLICABLE_LABEL,
  PAINT_BLOCK_KEY,
  PIT_TYPE,
  UNCERTAIN_PROFILE_LABEL,
} from '@/constants';
import {
  formatEnumForDisplay,
  titleCaseSentence,
  formatTimeStamps,
  formatFieldValue,
} from '@/utils';
import { usePoiStatusLabels } from '@/hooks/usePoiStatusLabels';
import { AllPointOfInterestTemplate, Columns, PointOfInterestsQueryType } from '@/types';
import {
  BlisterHeightType,
  UnitsEnum,
  UnitSystemEnum,
  VisibilityEnum,
} from '@/__generated__/graphql';
import { formatPercentage, localiseMeasurement } from '@/utils/unitSystem';
import { useGetStructureId } from '@/hooks/useGetStructureId';
import { checkIfVoi } from '@/utils/poi';
import { structureBlisterHeightType as structureBlisterHeightTypeAtom } from '@/components/Analysis/state';

export type RowData = {
  id: string;
  poiId: string;
  name: string;
  assembly: string;
  assemblyId: string;
  visibility: string;
  paintRegion: string;
  parentPaintRegionId: string;
  comment: string;
  isVerified: string;
  accessibility: string;
  status: string;
  isManuallyCreated: string;
  imageName: string;
  imageLink: string;
  createdAt: string;
  dueDate: string;
  isUncontextualized: boolean;
  fov: number;
  centerPoint3d: string;
  poiTemplateKey?: string | undefined;
  workpackNumber?: string;
  pdrHeight?: string;
  heightType?: string;
  estimatedHeight?: string;
  [key: string]: string | number | boolean | undefined;
};

type PoiRowProps = {
  pointOfInterests: PointOfInterestsQueryType;
  unitSystem: UnitSystemEnum;
  // getUnit: (value: number | undefined, percentage: number | undefined) => string;
  structureId: string;
  allVoiTemplates: AllPointOfInterestTemplate;
  customBlocksEnabled: boolean;
  customBlocksNames: string[];
  getPoiStatusLabel: (status: string) => string;
  structureBlisterHeightType: BlisterHeightType | undefined;
};

type PoiColsProps = {
  columns: Columns[];
  hiddenColumns: string[];
  rows: RowData[];
};

type PoiDataProps = {
  columns: Columns[];
  hiddenColumns: string[];
  pointOfInterests: PointOfInterestsQueryType;
};

const generateRows = ({
  pointOfInterests,
  unitSystem,
  structureId,
  allVoiTemplates,
  customBlocksEnabled,
  customBlocksNames,
  getPoiStatusLabel,
  structureBlisterHeightType,
}: PoiRowProps): RowData[] => {
  return (pointOfInterests ?? []).map((pointOfInterest) => {
    const {
      id,
      name,
      poiId,
      type: poiType,
      status,
      template,
      templateFieldData,
      visibility,
      paintRegion,
      parentPaintRegionId,
      comment,
      isVerified,
      accessibility,
      blister,
      assembly: { id: assemblyId, tagName },
      isManuallyCreated,
      location,
      centerPoint3d,
      createdAt,
      dueDate,
      isGoverning,
      deckRelativeHeight,
      isAutoGenerated,
    } = pointOfInterest;

    const { x, y, z } = centerPoint3d ?? {};
    const centerPoint3dExists = ![x, y, z].includes(undefined);

    const isUncontextualized = !centerPoint3dExists;
    const isVoi = checkIfVoi(allVoiTemplates, poiType);
    const imageViewExists = location?.name;

    const { name: templateName = '', fields: templateFields = [] } = template ?? {};
    const isPaintBlockVisibleInTemplate = templateFields.some(
      (f) => f.key === PAINT_BLOCK_KEY && f.defaultVisible === true
    );
    const rowData: RowData = {
      id,
      isGoverning: isGoverning === undefined ? 'N/A' : isGoverning ? 'Yes' : 'No',
      type: templateName,
      poiId: poiId || NOT_APPLICABLE_LABEL,
      name: name || NOT_APPLICABLE_LABEL,
      assembly: tagName,
      assemblyId,
      visibility: titleCaseSentence(visibility ?? VisibilityEnum.Public),
      paintRegion: paintRegion?.name ?? '',
      parentPaintRegionId: parentPaintRegionId?.name ?? '',
      comment: comment ?? '',
      isVerified: isVerified ? 'true' : 'false',
      accessibility: formatEnumForDisplay(accessibility),
      status: status ? getPoiStatusLabel(status) : NOT_APPLICABLE_LABEL,
      isManuallyCreated: isManuallyCreated ? 'true' : 'false',
      workpackNumber: pointOfInterest.workpack?.name || NOT_APPLICABLE_LABEL,
      imageName: location?.name ?? NOT_APPLICABLE_LABEL,
      imageLink: NOT_APPLICABLE_LABEL,
      createdAt: formatTimeStamps(createdAt ?? '', 'dd/MMM/yyyy'),
      dueDate: formatTimeStamps(dueDate ?? '', 'dd/MMM/yyyy'),
      isUncontextualized,
      fov: 0,
      centerPoint3d: centerPoint3dExists ? JSON.stringify([x, y, z]) : '',
      deck: paintRegion?.deck.name ?? NOT_APPLICABLE_LABEL,
      deckRelativeHeight: deckRelativeHeight ? deckRelativeHeight.toFixed(2) : NOT_APPLICABLE_LABEL,
      poiTemplateKey: template?.key || NOT_APPLICABLE_LABEL,
    };

    if (
      customBlocksEnabled &&
      !isPaintBlockVisibleInTemplate &&
      pointOfInterest.paintRegions &&
      customBlocksNames.length > 0
    ) {
      pointOfInterest.paintRegions.forEach((paintRegion) => {
        const groupName = paintRegion.paintRegionGroup?.name;
        if (groupName && customBlocksNames.includes(groupName)) {
          rowData[groupName] = paintRegion.name || NOT_APPLICABLE_LABEL;
        }
      });
    }
    // show paint blocks ande deck in poi table from poi template
    if (templateFieldData[PAINT_BLOCK_KEY]) {
      rowData[PAINT_BLOCK_KEY] = templateFieldData[PAINT_BLOCK_KEY];
    }
    if (templateFieldData[DECK_KEY]) {
      rowData[DECK_KEY] = templateFieldData[DECK_KEY];
    }
    if (!isUncontextualized) {
      const fov = isVoi ? 0 : imageViewExists ? 0 : 2;
      rowData.fov = fov;
      const lookAt = `&look.x=${x}&look.y=${y}&look.z=${z}`;
      const position = `&position.x=${imageViewExists ? x : 0}&position.y=${
        imageViewExists ? y : 0
      }&position.z=${imageViewExists ? z : 0}`;
      rowData.imageLink = `${window.location.origin}/analysis/${structureId}/viewer?assembly=${rowData.assembly}&poi=${rowData.id}${lookAt}${position}&fov=${fov}`;
      if (imageViewExists) rowData.imageLink += `&selectedSpherical=${rowData.imageName}`;
    }

    Object.keys(templateFieldData).forEach((key) => {
      const fieldUnit = templateFields.find((item) => item.key === key)?.unit;
      const fieldType = templateFields.find((item) => item.key === key)?.type;
      rowData[key] = formatFieldValue({
        unitSystem,
        type: fieldType,
        unit: fieldUnit ?? undefined,
        value: templateFieldData[key] ?? '',
        showUnitLabel: true,
      });
    });

    if ((poiType === BLISTER_TYPE || poiType === PIT_TYPE) && blister) {
      const {
        maxHeight,
        wallLoss,
        residualCorrosionAllowance,
        residualThickness,
        pdrHeight,
        heightType,
        estimatedHeight,
      } = blister;

      // for maximum blister height
      rowData.maxHeight = localiseMeasurement({
        unitSystem,
        value: !isNaN(Number(maxHeight)) ? Number(maxHeight) : undefined,
        sourceUnit: UnitsEnum.Meter,
        displayAsUnit: UnitsEnum.Millimeter,
        fallbackLabel: UNCERTAIN_PROFILE_LABEL,
      });

      const wallLossValue = wallLoss?.value;
      const wallLossPercentage = wallLoss?.percentage;
      rowData.estimatedWallLoss =
        wallLossValue === null ||
        wallLossValue === undefined ||
        wallLossPercentage === null ||
        wallLossPercentage === undefined
          ? NOT_APPLICABLE_LABEL
          : localiseMeasurement({
              unitSystem,
              value: Number(wallLossValue),
              sourceUnit: UnitsEnum.Meter,
              displayAsUnit: UnitsEnum.Millimeter,
              showUnitLabel: false,
            }) + formatPercentage(Number(wallLossPercentage));

      const residualCorrosionAllowanceValue = residualCorrosionAllowance?.value;
      const residualCorrosionAllowancePercentage = residualCorrosionAllowance?.percentage;
      rowData.residualCorrosionAllowance =
        residualCorrosionAllowanceValue === null ||
        residualCorrosionAllowanceValue === undefined ||
        residualCorrosionAllowancePercentage === null ||
        residualCorrosionAllowancePercentage === undefined
          ? NOT_APPLICABLE_LABEL
          : localiseMeasurement({
              unitSystem,
              value: Number(residualCorrosionAllowanceValue),
              sourceUnit: UnitsEnum.Meter,
              displayAsUnit: UnitsEnum.Millimeter,
              showUnitLabel: false,
            }) + formatPercentage(Number(residualCorrosionAllowancePercentage));

      // for residual thickness
      const residualThicknessValue = residualThickness?.value;
      const residualThicknessPercentage = residualThickness?.percentage;
      rowData.residualThickness =
        residualThicknessValue === null ||
        residualThicknessValue === undefined ||
        residualThicknessPercentage === null ||
        residualThicknessPercentage === undefined
          ? NOT_APPLICABLE_LABEL
          : localiseMeasurement({
              unitSystem,
              value: Number(residualThicknessValue),
              sourceUnit: UnitsEnum.Meter,
              displayAsUnit: UnitsEnum.Millimeter,
              showUnitLabel: false,
            }) + formatPercentage(Number(residualThicknessPercentage));

      rowData.measurementConfidence = blister.measurementConfidence ?? NOT_APPLICABLE_LABEL;

      if (isAutoGenerated) {
        rowData.estimatedHeight = localiseMeasurement({
          unitSystem,
          value: !isNaN(Number(estimatedHeight)) ? Number(estimatedHeight) : undefined,
          sourceUnit: UnitsEnum.Meter,
          displayAsUnit: UnitsEnum.Millimeter,
          fallbackLabel: UNCERTAIN_PROFILE_LABEL,
        });
        rowData.pdrHeight = localiseMeasurement({
          unitSystem,
          value: !isNaN(Number(pdrHeight)) ? Number(pdrHeight) : undefined,
          sourceUnit: UnitsEnum.Meter,
          displayAsUnit: UnitsEnum.Millimeter,
          fallbackLabel: UNCERTAIN_PROFILE_LABEL,
        });
        rowData.heightType = heightType
          ? heightTypeMapping[heightType as keyof typeof heightTypeMapping]
          : structureBlisterHeightType
          ? heightTypeMapping[structureBlisterHeightType as keyof typeof heightTypeMapping]
          : NOT_APPLICABLE_LABEL;
      }
    }

    return rowData;
  });
};

const generateCols = ({ columns, rows, hiddenColumns }: PoiColsProps) => {
  if (rows) {
    return columns
      .filter(
        (column) =>
          !column.visibility || (column.visibility && !hiddenColumns?.includes(column?.key))
      )
      .map((col) => ({
        label: col.columnName ?? 'no label',
        data: rows.map((row) => row[col.key] || ''),
      }));
  }
  return [];
};

const usePoiData = ({ pointOfInterests, columns, hiddenColumns }: PoiDataProps) => {
  const unitSystem = useAtomValue(unitSystemState);
  const allVoiTemplates = useAtomValue(allVoiTemplatesState);
  const customBlocksEnabled = useAtomValue(customBlocksEnabledState);
  const customBlocksNames = useAtomValue(customBlocksNamesState);
  const structureBlisterHeightType = useAtomValue(structureBlisterHeightTypeAtom);
  const structureId = useGetStructureId();
  const { getPoiStatusLabel } = usePoiStatusLabels();

  const rows = useMemo(
    () =>
      generateRows({
        pointOfInterests,
        unitSystem,
        structureId,
        allVoiTemplates,
        customBlocksEnabled,
        customBlocksNames,
        getPoiStatusLabel,
        structureBlisterHeightType,
      }),
    [
      pointOfInterests,
      unitSystem,
      structureId,
      allVoiTemplates,
      customBlocksEnabled,
      customBlocksNames,
      getPoiStatusLabel,
    ]
  );

  const cols = useMemo(
    () => generateCols({ columns, rows, hiddenColumns }),
    [columns, rows, hiddenColumns]
  );

  return { rows, cols };
};

export default usePoiData;
